// shared/pricing.ts
//
// Tételes, ÁFA-tudatos lakás-árazás — EGYETLEN közös számítási hely, amit az
// admin (project-edit), a publikus lakás-oldal (unit-detail), az
// összehasonlítás (unit-compare), a szűrő (unit-filter) és a hitel-kalkulátor
// (mortgage-calculator) egyaránt használ. Nincs benne framework- vagy
// DB-függőség, így a client bundle-be is bekerülhet. A típusokat innen importálja
// a schema.ts (a JSONB oszlopokhoz), hogy a client ne húzza be a drizzle-t.

/**
 * Egy ár-tétel fajtája. A `*_sqm` tételek mennyisége a lakás megfelelő m²
 * mezőjéből jön (egyetlen igazságforrás); az `extra` (garázs / parkolóhely /
 * tároló) darabáras.
 */
export type PriceItemKind =
  | "base_sqm"
  | "balcony_sqm"
  | "terrace_sqm"
  | "garden_sqm"
  | "extra";

/**
 * A m²-kötés jelzője (F-MANUAL-TSNIZ4OY). Ha egy tételen be van állítva, a
 * tétel mennyisége compute-időben MINDIG a lakás friss m² mezőiből jön (nem a
 * tárolt `quantity`-ből), így a lakás/erkély m² átírására élőben újraszámol:
 *  - `"area"`          → a lakás alapterülete (areaSqm)
 *  - `"balconyTerrace"`→ az erkély + terasz m² összege (balcony + terrace),
 *    egyetlen közös m²-áron (az „Erkély / terasz m²-ár" alapérték szerint).
 */
export type BoundToM2 = "area" | "balconyTerrace";

/** Egy lakás egy ár-tétele (JSONB-ben tárolva a project_units.price_items-ben). */
export type PriceItem = {
  kind: PriceItemKind;
  /** Megjelenített megnevezés; üresen a fajta alapértelmezett címkéje. */
  label?: string;
  /** Ha meg van adva, a tétel mennyisége a lakás friss m² mezőjéből számol
   *  (élő újraszámítás), nem a tárolt `quantity`-ből. Additív, visszafelé
   *  kompatibilis: hiányában a régi `kind`-alapú kötés érvényes. */
  boundToM2?: BoundToM2 | null;
  /** Egységár Ft-ban: m²-ár (`*_sqm`) vagy darab-/m²-ár (`extra`). */
  unitPriceHuf: number;
  /** Csak `extra`: mennyiség — darab, vagy `unit: "sqm"` esetén m² (tört is).
   *  m²-fajtájú tételnél a mennyiség a lakás m² mezőjéből jön. */
  quantity?: number;
  /** Csak `extra`: a mennyiség egysége — "db" (alapértelmezett) vagy "sqm"
   *  (pl. tároló m²-ár alapon). */
  unit?: "db" | "sqm";
  /** Az egységár bruttó (true) vagy nettó (false) értendő-e. */
  isGross: boolean;
  /** ÁFA-kulcs százalékban, pl. 5 vagy 27. */
  vatPct: number;
  /** Nettó árnál: +ÁFA hozzáadandó-e (true) vagy az ár végleges/ÁFA-mentes (false). */
  plusVat: boolean;
  /** Csak `extra`: opcionálisan megvásárolható (nem része a lakás alapárának). */
  optional?: boolean;
};

/** Projekt-szintű árazási alapértékek — új lakásnál előtöltéshez. */
export type PricingDefaults = {
  /** Alap m²-ár (lakás alapterület), Ft/m². */
  baseSqmPriceHuf?: number | null;
  /** Erkély / terasz m²-ára, Ft/m². */
  outdoorSqmPriceHuf?: number | null;
  /** Alap ÁFA-kulcs (%). */
  vatPct?: number | null;
  /** Az alap-egységárak bruttók-e. */
  isGross?: boolean;
  /** Nettó alapároknál +ÁFA értendő-e. */
  plusVat?: boolean;
  /** Kézi kapcsoló: a projekt lakásainál NE jelenjen meg ár a publikus
   *  oldalon (és az AI-chatben) — az ár helyén „Kérjen ajánlatot" áll.
   *  Független a referencia áraktól: bekapcsolva akkor is rejt, ha vannak
   *  m²-árak; kikapcsolva visszahozza őket. Lásd `arePricesHidden`. */
  hidePrices?: boolean | null;
};

/** Az ár helyén megjelenő hívás, ha a projektnél nincs publikus ár. */
export const REQUEST_QUOTE_LABEL = "Kérjen ajánlatot";

/**
 * Elrejtendők-e a projekt lakásainak árai a PUBLIKUS felületeken (lakáslista,
 * lakásoldal, összehasonlítás, szűrő, épület-nézet, AI-chat)? Kétféleképpen
 * kapcsolható:
 *
 *  1. **Referencia árak törlése** — ha a projekt árazási alapértékeiben sem az
 *     alap m²-ár, sem az erkély/terasz m²-ár nincs megadva (vagy 0), a projekt
 *     „ár nélküli": a lakásoknál beragadt, korábban felvett tételes/kézi árak
 *     sem látszanak. Így egy helyen, egy mozdulattal kikapcsolható az árazás.
 *  2. **Kézi kapcsoló** (`hidePrices`) — az admin explicit módon elrejtheti az
 *     árakat akkor is, ha a referencia árak megvannak, és bármikor visszavonhatja.
 *
 * FONTOS: ez csak a MEGJELENÍTÉST szabályozza — sem az adatokat, sem a
 * `computeUnitPrice` számítását nem írja át, így az admin felületen minden ár
 * változatlanul látszik és visszakapcsolásnál azonnal visszatér.
 */
export function arePricesHidden(defaults?: PricingDefaults | null): boolean {
  if (defaults?.hidePrices) return true;
  const base = num(defaults?.baseSqmPriceHuf) ?? 0;
  const outdoor = num(defaults?.outdoorSqmPriceHuf) ?? 0;
  return base <= 0 && outdoor <= 0;
}

export const PRICE_ITEM_KINDS: PriceItemKind[] = [
  "base_sqm",
  "balcony_sqm",
  "terrace_sqm",
  "garden_sqm",
  "extra",
];

const KIND_LABEL_HU: Record<PriceItemKind, string> = {
  base_sqm: "Lakás alapterület",
  balcony_sqm: "Erkély",
  terrace_sqm: "Terasz",
  garden_sqm: "Saját kert",
  extra: "Extra",
};

/** A tétel-fajta alapértelmezett magyar címkéje. */
export function priceItemKindLabel(kind: PriceItemKind): string {
  return KIND_LABEL_HU[kind] ?? "Tétel";
}

/** true, ha a tétel mennyisége a lakás m² mezőjéből számolódik. */
export function isSqmKind(kind: PriceItemKind): boolean {
  return kind !== "extra";
}

/** A lakásból számításhoz kellő mezők (numeric() oszlopok stringként jönnek). */
export type UnitPriceInput = {
  priceHuf?: number | null;
  /** Kézi ár-felülírás (Ft, bruttó). Ha meg van adva (nem null, ≥ 0), ez NYER
   *  a tételes/fallback számítás felett — ez lesz a fejléc-bruttó mindenhol. */
  priceOverrideHuf?: number | null;
  areaSqm?: number | string | null;
  balconyAreaSqm?: number | string | null;
  terraceAreaSqm?: number | string | null;
  gardenAreaSqm?: number | string | null;
  priceItems?: PriceItem[] | null;
  /** A projekt árazási alapértékei (Ft/m²-árak, ÁFA). Ha meg van adva ÉS a
   *  lakásnak nincs saját, m²-alapú (kötelező) tétele, a lakás alapára
   *  compute-időben AUTOMATIKUSAN a m²-ből számol: alapterület × alap-m²-ár
   *  (+ erkély+terasz × kültéri-m²-ár). Így minden lakás azonnal m²-alapon
   *  árazódik, kézi tétel-felvétel nélkül; a kézi felülírás (priceOverrideHuf)
   *  és a saját tételek továbbra is nyernek. Alapérték nélkül a régi
   *  viselkedés marad (priceHuf fallback). */
  pricingDefaults?: PricingDefaults | null;
};

/** Egy kiszámolt ár-sor (a tétel + a levezetett nettó/ÁFA/bruttó). */
export type PriceLine = {
  kind: PriceItemKind;
  label: string;
  /** Tényleges mennyiség: m² (m²-tétel) vagy darab (extra). */
  quantity: number;
  /** Mértékegység a megjelenítéshez. */
  unit: "sqm" | "db";
  unitPriceHuf: number;
  vatPct: number;
  isGross: boolean;
  plusVat: boolean;
  optional: boolean;
  netHuf: number;
  vatHuf: number;
  grossHuf: number;
};

/** Egy lakás teljes, számított árazása. */
export type UnitPrice = {
  /** true, ha tételekből számolt (nem a régi priceHuf fallback). */
  hasItems: boolean;
  /** true, ha egyáltalán van ár (tételes VAGY régi priceHuf VAGY kézi ár). */
  hasPrice: boolean;
  /** true, ha kézi ár-felülírás (priceOverrideHuf) van érvényben — ekkor a
   *  fejléc net/vat/gross a kézi bruttót tükrözi, nem a tételes számítást. */
  hasOverride: boolean;
  // Fejléc-ár = a kötelező (nem opcionális) tételek összege — ezt látja a
  // publikus oldal, az összehasonlítás, a szűrő és a hitel-kalkulátor.
  netHuf: number;
  vatHuf: number;
  grossHuf: number;
  // Opcionális extrák (külön megvásárolható garázs / parkoló / tároló).
  optionalNetHuf: number;
  optionalVatHuf: number;
  optionalGrossHuf: number;
  // Nagy végösszeg (kötelező + opcionális) — az admin nézethez.
  totalNetHuf: number;
  totalVatHuf: number;
  totalGrossHuf: number;
  lines: PriceLine[];
};

/** Szám kinyerése numeric() stringből / számból; érvénytelen → null. */
function num(v: number | string | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === "string" ? Number(v.replace(/\s+/g, "").replace(/,/g, ".")) : v;
  return Number.isFinite(n) ? n : null;
}

function areaForKind(input: UnitPriceInput, kind: PriceItemKind): number {
  switch (kind) {
    case "base_sqm":
      return num(input.areaSqm) ?? 0;
    case "balcony_sqm":
      return num(input.balconyAreaSqm) ?? 0;
    case "terrace_sqm":
      return num(input.terraceAreaSqm) ?? 0;
    case "garden_sqm":
      return num(input.gardenAreaSqm) ?? 0;
    default:
      return 0;
  }
}

/** A m²-kötött tétel mennyisége a lakás friss m² mezőiből (F-MANUAL-TSNIZ4OY). */
export function boundAreaSqm(input: UnitPriceInput, bound: BoundToM2): number {
  if (bound === "area") return num(input.areaSqm) ?? 0;
  // balconyTerrace: erkély + terasz összege (egy közös m²-ár).
  return (num(input.balconyAreaSqm) ?? 0) + (num(input.terraceAreaSqm) ?? 0);
}

function isBoundToM2(v: unknown): v is BoundToM2 {
  return v === "area" || v === "balconyTerrace";
}

/** A m²-kötött tétel alapértelmezett magyar címkéje. */
export function boundToM2Label(bound: BoundToM2): string {
  return bound === "area" ? "Lakás alapterület" : "Erkély / terasz";
}

/**
 * Egy tétel tényleges mennyisége compute-időben. A m²-kötés (`boundToM2`) NYER:
 * a mennyiség a lakás friss m² mezőiből jön. Kötés hiányában a régi viselkedés:
 * `*_sqm` fajta → a megfelelő m² mező; `extra` → a tárolt `quantity`.
 */
function quantityForItem(input: UnitPriceInput, item: PriceItem): number {
  if (isBoundToM2(item.boundToM2)) {
    return boundAreaSqm(input, item.boundToM2);
  }
  if (isSqmKind(item.kind)) return areaForKind(input, item.kind);
  const extraSqm = item.kind === "extra" && item.unit === "sqm";
  return extraSqm
    ? Math.max(0, num(item.quantity) ?? 0)
    : Math.max(0, Math.trunc(num(item.quantity) ?? 1));
}

/** true, ha a tétel az alapár (required) része — a m²-kötött és a `*_sqm`
 *  tételek igen; az extra (garázs/parkoló/tároló) opcionális. */
function isRequiredItem(item: PriceItem): boolean {
  return isBoundToM2(item.boundToM2) || item.kind !== "extra";
}

/** Egy tétel nettó/ÁFA/bruttó összege adott mennyiségnél, Ft-ra kerekítve.
 *  A bruttó és a nettó külön kerekül; az ÁFA a kettő különbsége, így a
 *  nettó + ÁFA = bruttó mindig pontosan igaz. */
export function computeLineAmounts(
  item: Pick<PriceItem, "unitPriceHuf" | "isGross" | "vatPct" | "plusVat">,
  quantity: number,
): { netHuf: number; vatHuf: number; grossHuf: number } {
  const rate = Math.max(0, num(item.vatPct) ?? 0) / 100;
  const unitPrice = num(item.unitPriceHuf) ?? 0;
  const qty = Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
  const stated = unitPrice * qty;
  let netRaw: number;
  let grossRaw: number;
  if (item.isGross) {
    // A megadott ár bruttó → a nettót visszaszámoljuk.
    grossRaw = stated;
    netRaw = rate > 0 ? stated / (1 + rate) : stated;
  } else {
    // A megadott ár nettó → +ÁFA ha kell, egyébként az ár a végleges.
    netRaw = stated;
    grossRaw = item.plusVat ? stated * (1 + rate) : stated;
  }
  const netHuf = Math.round(netRaw);
  const grossHuf = Math.round(grossRaw);
  return { netHuf, grossHuf, vatHuf: grossHuf - netHuf };
}

function isKnownKind(k: unknown): k is PriceItemKind {
  return typeof k === "string" && (PRICE_ITEM_KINDS as string[]).includes(k);
}

/**
 * A projekt árazási alapértékeiből (m²-árak) származtatott, m²-kötött alap-
 * tételek — akkor kerülnek használatba, ha a lakásnak NINCS saját, kötelező
 * m²-alaptétele. Így minden lakás azonnal m²-alapon árazódik, kézi tétel-
 * felvétel nélkül. Csak pozitív m² ÉS pozitív egységár esetén keletkezik tétel
 * (üres/nulla sor nem), és a mennyiség compute-időben a friss m² mezőkből jön.
 */
function deriveDefaultBaseItems(input: UnitPriceInput): PriceItem[] {
  const d = input.pricingDefaults;
  if (!d) return [];
  const out: PriceItem[] = [];
  const baseRate = num(d.baseSqmPriceHuf) ?? 0;
  const area = num(input.areaSqm) ?? 0;
  if (baseRate > 0 && area > 0) out.push(boundPriceItem("area", d));
  const outdoorRate = num(d.outdoorSqmPriceHuf) ?? 0;
  const outdoor = (num(input.balconyAreaSqm) ?? 0) + (num(input.terraceAreaSqm) ?? 0);
  if (outdoorRate > 0 && outdoor > 0) out.push(boundPriceItem("balconyTerrace", d));
  return out;
}

/**
 * A lakás teljes árazásának kiszámítása. Ha nincs (érvényes, pozitív árú)
 * tétel, visszaesik a régi `priceHuf` mezőre (bruttóként kezelve) — így a
 * tételek nélküli lakások a mai módon működnek.
 *
 * Kézi ár-felülírás (`priceOverrideHuf`): ha meg van adva (nem null, ≥ 0), ez
 * NYER a tételes/fallback számítás felett — a fejléc-bruttó (és minden
 * megjelenített/mentett ár) a kézi értéket tükrözi. A tételes `lines` bontás
 * ilyenkor is kiszámolódik (referencia/admin nézethez), de a fejlécet a kézi
 * ár adja. Az opcionális extrák (garázs/parkoló/tároló) továbbra is a
 * tételekből számolnak, a kézi alapár mellé.
 */
export function computeUnitPrice(input: UnitPriceInput): UnitPrice {
  // Kézi felülírás: érvényes, ha nem null és nem negatív. A 0 is érvényes
  // (pl. "0 Ft"), ezért null-ellenőrzés kell, nem truthy-teszt.
  const override = num(input.priceOverrideHuf);
  const hasOverride = override != null && override >= 0;

  const explicitItems = (
    Array.isArray(input.priceItems) ? input.priceItems : []
  ).filter(
    (it): it is PriceItem => !!it && isKnownKind(it.kind) && (num(it.unitPriceHuf) ?? 0) > 0,
  );

  // Ha a lakásnak van saját, kötelező m²-alaptétele, azt használjuk (kézi
  // tételezés nyer). Egyébként a projekt árazási alapértékeiből származtatunk
  // m²-kötött alap-tételt, hogy a lakás azonnal m²-alapon árazódjon. A
  // származtatott tételek a lista ELEJÉRE kerülnek (a bontásban felül).
  const derived = explicitItems.some(isRequiredItem)
    ? []
    : deriveDefaultBaseItems(input);
  const items = [...derived, ...explicitItems];

  if (items.length === 0) {
    const price = num(input.priceHuf);
    // Kézi ár nyer; egyébként a régi priceHuf fallback (bruttóként).
    const gross = hasOverride ? override! : (price ?? 0);
    return {
      hasItems: false,
      hasPrice: hasOverride || price != null,
      hasOverride,
      netHuf: gross,
      vatHuf: 0,
      grossHuf: gross,
      optionalNetHuf: 0,
      optionalVatHuf: 0,
      optionalGrossHuf: 0,
      totalNetHuf: gross,
      totalVatHuf: 0,
      totalGrossHuf: gross,
      lines: [],
    };
  }

  const lines: PriceLine[] = items.map((item) => {
    // A mennyiség compute-időben: m²-kötés (boundToM2) NYER a friss m² mezőből,
    // egyébként a régi kind-alapú kötés (*_sqm) vagy az extra tárolt darabszáma.
    const qty = quantityForItem(input, item);
    // m²-alapú (kötött VAGY *_sqm) és a m²-egységű extra „sqm" mértékegységet
    // mutat; a darabáras extra „db"-ot.
    const sqmUnit =
      isBoundToM2(item.boundToM2) ||
      isSqmKind(item.kind) ||
      (item.kind === "extra" && item.unit === "sqm");
    // A m²-kötött és a *_sqm tételek az alapár részei; az extra (garázs /
    // parkoló / tároló) MINDIG opcionális — a lakás árához sosem adódik hozzá
    // (megrendelői döntés, F-MANUAL-TCKB9TGH komment), kivéve ha m²-kötött.
    const optional = !isRequiredItem(item);
    const label =
      (item.label ?? "").trim() ||
      (isBoundToM2(item.boundToM2)
        ? boundToM2Label(item.boundToM2)
        : priceItemKindLabel(item.kind));
    return {
      kind: item.kind,
      label,
      quantity: qty,
      unit: sqmUnit ? "sqm" : "db",
      unitPriceHuf: num(item.unitPriceHuf) ?? 0,
      vatPct: num(item.vatPct) ?? 0,
      isGross: !!item.isGross,
      plusVat: !!item.plusVat,
      optional,
      ...computeLineAmounts(item, qty),
    };
  });

  let reqNet = 0;
  let reqGross = 0;
  let optNet = 0;
  let optGross = 0;
  let hasRequired = false;
  for (const l of lines) {
    if (l.optional) {
      optNet += l.netHuf;
      optGross += l.grossHuf;
    } else {
      hasRequired = true;
      reqNet += l.netHuf;
      reqGross += l.grossHuf;
    }
  }

  // CSAK extra tételek (kötelező alap-tétel nincs): a lakás ára NEM 0, hanem
  // a régi priceHuf fallback marad — az extrák csak külön megvásárolható
  // tételekként jelennek meg mellette (F-MANUAL-TCKB9TGH tesztelői bug).
  if (!hasRequired) {
    const fallback = num(input.priceHuf) ?? 0;
    reqNet = fallback;
    reqGross = fallback;
  }

  // Kézi ár-felülírás nyer az alapár (required) felett: a fejléc-bruttó a kézi
  // érték lesz, ÁFA-bontás nélkül (net = gross, vat = 0), mert a kézi ár egy
  // végleges bruttó. Az opcionális extrák változatlanul a tételekből számolnak.
  const baseNet = hasOverride ? override! : reqNet;
  const baseGross = hasOverride ? override! : reqGross;

  return {
    hasItems: true,
    hasPrice: true,
    hasOverride,
    netHuf: baseNet,
    vatHuf: baseGross - baseNet,
    grossHuf: baseGross,
    optionalNetHuf: optNet,
    optionalVatHuf: optGross - optNet,
    optionalGrossHuf: optGross,
    totalNetHuf: baseNet + optNet,
    totalVatHuf: baseGross + optGross - (baseNet + optNet),
    totalGrossHuf: baseGross + optGross,
    lines,
  };
}

/**
 * A lakás megjelenített (bruttó, fejléc) ára, vagy null ha egyáltalán nincs ár.
 * Ezt használja az összehasonlítás, a szűrő, a kártyák és a hitel-kalkulátor,
 * hogy a "—" (nincs ár) eset megmaradjon.
 */
export function unitDisplayPriceHuf(input: UnitPriceInput): number | null {
  const p = computeUnitPrice(input);
  if (!p.hasItems && !p.hasPrice) return null;
  return p.grossHuf;
}

/** Új ár-tétel a projekt alapértékeiből előtöltve (admin "tétel hozzáadása"). */
export function defaultPriceItem(
  kind: PriceItemKind,
  defaults?: PricingDefaults | null,
  /** Extra tételnél előtöltött megnevezés (pl. "Garázs"). */
  label = "",
): PriceItem {
  const vatPct = num(defaults?.vatPct) ?? (kind === "extra" ? 27 : 5);
  const isGross = defaults?.isGross ?? true;
  const plusVat = defaults?.plusVat ?? false;
  if (kind === "extra") {
    return { kind, label, unitPriceHuf: 0, quantity: 1, unit: "db", isGross, vatPct, plusVat, optional: true };
  }
  const unitPriceHuf =
    kind === "base_sqm"
      ? (num(defaults?.baseSqmPriceHuf) ?? 0)
      : (num(defaults?.outdoorSqmPriceHuf) ?? 0);
  return { kind, unitPriceHuf, isGross, vatPct, plusVat };
}

/**
 * m²-kötött ár-tétel a projekt alapértékeiből (F-MANUAL-TSNIZ4OY). A darabszám
 * compute-időben a lakás friss m² mezőjéből jön (`boundToM2`), az egységár az
 * alapértékből (alap-m²-ár / erkély-terasz-m²-ár). Ezt hozza létre a
 * „Kitöltés alapértékekből" gomb, hogy a lakás/erkély m² átírására élőben
 * újraszámoljon.
 */
export function boundPriceItem(
  bound: BoundToM2,
  defaults?: PricingDefaults | null,
): PriceItem {
  const vatPct = num(defaults?.vatPct) ?? 5;
  const isGross = defaults?.isGross ?? true;
  const plusVat = defaults?.plusVat ?? false;
  const unitPriceHuf =
    bound === "area"
      ? (num(defaults?.baseSqmPriceHuf) ?? 0)
      : (num(defaults?.outdoorSqmPriceHuf) ?? 0);
  // A kind visszafelé kompatibilis címkézéshez marad; a kötést a boundToM2 adja.
  const kind: PriceItemKind = bound === "area" ? "base_sqm" : "balcony_sqm";
  return { kind, boundToM2: bound, unitPriceHuf, isGross, vatPct, plusVat };
}
